import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Brain, Hourglass, Repeat, Clock, Target, Sparkles } from "lucide-react";
import { PAIN_POINTS, MENTAL_LOAD } from "../config/brand";
import { FadeUp, LineReveal, Eyebrow } from "../components/Reveal";

const icons = [Brain, Hourglass, Repeat, Clock, Target, Sparkles];

const POSITIONS = [
  { left: "4%", top: "6%" }, { left: "64%", top: "2%" }, { left: "30%", top: "12%" },
  { left: "76%", top: "26%" }, { left: "2%", top: "34%" }, { left: "56%", top: "42%" },
  { left: "10%", top: "58%" }, { left: "72%", top: "64%" }, { left: "34%", top: "72%" },
  { left: "58%", top: "84%" },
];

const MentalLoad = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const gather = useTransform(scrollYProgress, [0.35, 0.75], [0, 1]);

  return (
    <div ref={ref} className="relative">
      <div className="overflow-hidden rounded-3xl border border-mauve/30">
        <img
          src="/assets/overwhelm.png"
          alt="A working mother pausing at a kitchen table full of the day's responsibilities"
          className="aspect-[4/3] w-full object-cover"
          width={800}
          height={600}
          loading="lazy"
        />
      </div>
      {MENTAL_LOAD.map((label, i) => {
        const pos = POSITIONS[i % POSITIONS.length];
        return (
          <GatherChip
            key={label}
            label={label}
            index={i}
            pos={pos}
            gather={gather}
            reduce={reduce}
          />
        );
      })}
    </div>
  );
};

const GatherChip = ({ label, index, pos, gather, reduce }) => {
  const opacity = useTransform(gather, [0, 1], [1, 0.25]);
  const scale = useTransform(gather, [0, 1], [1, 0.8]);
  return (
    <motion.span
      aria-hidden="true"
      initial={reduce ? false : { opacity: 0, y: 14, scale: 0.9 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: 0.15 + index * 0.09, ease: [0.22, 1, 0.36, 1] }}
      style={{ left: pos.left, top: pos.top, opacity, scale }}
      className="absolute rounded-lg border border-plum/15 bg-warmwhite/95 px-2.5 py-1 font-serif text-xs italic text-plum shadow-[0_6px_18px_rgba(90,48,73,0.12)] backdrop-blur-sm"
    >
      <motion.span
        className="block"
        animate={reduce ? undefined : { y: [0, -4, 0] }}
        transition={{ duration: 3 + (index % 4) * 0.7, repeat: Infinity, ease: "easeInOut", delay: index * 0.3 }}
      >
        {label}
      </motion.span>
    </motion.span>
  );
};

export const Problem = () => (
  <section className="relative bg-ivory" aria-label="The problem" data-testid="problem-section">
    <div className="mx-auto max-w-[1280px] px-5 py-20 sm:px-8 lg:py-28">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-5">
          <FadeUp>
            <Eyebrow>If you're tired of holding everything together</Eyebrow>
          </FadeUp>
          <LineReveal
            as="h2"
            lines={["You're managing a career,", "a family and a life", "of your own."]}
            className="mt-4 font-serif text-4xl font-medium leading-[1.08] tracking-tight text-plum sm:text-5xl"
          />
          <FadeUp delay={0.15} className="mt-6 space-y-4 text-base leading-relaxed text-plumcharcoal/75">
            <p>
              Your calendar is full. Your kids need you. Work keeps moving. The
              house still needs managing. Meals still need planning.
            </p>
            <p>
              And somewhere underneath all of it are the goals you keep telling
              yourself you'll get back to.
            </p>
          </FadeUp>
          <FadeUp delay={0.25} className="mt-8 hidden lg:block">
            <MentalLoad />
            <p className="mt-4 text-center font-serif text-lg italic text-rose">
              You don't need to hold everything in your head.
            </p>
          </FadeUp>
          <div className="mt-6 flex flex-wrap gap-2 lg:hidden" aria-hidden="true">
            {MENTAL_LOAD.map((label, i) => (
              <FadeUp key={label} delay={0.1 + i * 0.05}>
                <span className="rounded-lg border border-mauve/40 bg-warmwhite px-3 py-1.5 font-serif text-sm italic text-plum shadow-sm">
                  {label}
                </span>
              </FadeUp>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {PAIN_POINTS.map((p, i) => {
              const Icon = icons[i];
              return (
                <FadeUp key={p.title} delay={i * 0.06}>
                  <article
                    data-testid={`pain-card-${i + 1}`}
                    className="group h-full rounded-3xl border border-mauve/35 bg-warmwhite p-7 shadow-[0_8px_30px_rgba(90,48,73,0.04)] transition-[box-shadow,transform,border-color] duration-300 hover:-translate-y-1.5 hover:border-rose/40 hover:shadow-[0_18px_48px_rgba(90,48,73,0.1)]"
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blushcream text-plum transition-colors duration-300 group-hover:bg-plum group-hover:text-ivory">
                      <Icon size={19} strokeWidth={1.5} />
                    </span>
                    <h3 className="mt-5 font-serif text-xl font-semibold tracking-tight text-plum">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-plumcharcoal/70">{p.text}</p>
                  </article>
                </FadeUp>
              );
            })}
          </div>
          <FadeUp delay={0.2}>
            <p className="mt-10 border-l-2 border-rose pl-5 font-serif text-2xl italic leading-snug text-plum sm:text-[1.7rem]">
              You don't need another impossible routine. You need a system that
              works with the life you actually have.
            </p>
          </FadeUp>
        </div>
      </div>
    </div>
  </section>
);
